import type { DescMessage, MessageInitShape } from '@bufbuild/protobuf';
import {
  GetUiStateRequestSchema,
  SetUiStateRequestSchema,
  UiStateResponseSchema,
} from './gen/ui_state_service_pb';
import { unaryViaDirectGrpcWeb } from './directGrpcWeb';
import { getGrpcBaseUrl } from './transport';
import { webLog } from './webLogger';

const SERVICE_PATH = '/pipeex.uistate.v1.UiStateService';

/**
 * Durable UI state bridge — mirrors small workflow-resume markers (see
 * shared/lib/durableState) into a plugin-owned JSON file outside the WebView2
 * user-data folder. The plugin's cache-version guard purges that folder on
 * every plugin version change, so localStorage alone cannot hold these
 * markers; this client is the re-hydration channel after such a purge.
 *
 * Every function resolves to `null`/`false` when the embedded gRPC server is
 * unavailable (standalone browser dev) or the call fails — callers must keep
 * working with localStorage-only state in that case.
 */
async function callUiStateRpc<Req extends DescMessage>(
  method: string,
  requestSchema: Req,
  requestInit: MessageInitShape<Req>
) {
  if (!getGrpcBaseUrl()) {
    // Standalone browser dev mode: no embedded plugin server to mirror into.
    return null;
  }

  try {
    return await unaryViaDirectGrpcWeb(
      `${SERVICE_PATH}/${method}`,
      requestSchema,
      UiStateResponseSchema,
      requestInit
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    webLog.warn('uiStateClient', `${method} failed: ${message}`);
    return null;
  }
}

/**
 * Read mirrored entries from the plugin-side store. An empty `keys` list
 * returns everything. Returns `null` when the bridge is unavailable.
 */
export async function fetchUiStateEntries(keys: string[]): Promise<Record<string, string> | null> {
  const response = await callUiStateRpc('GetUiState', GetUiStateRequestSchema, { keys });
  if (!response) {
    return null;
  }

  return { ...(response.entries ?? {}) };
}

/**
 * Upsert `entries` and delete `removeKeys` in the plugin-side store.
 * Resolves to `true` only when the bridge acknowledged the write.
 */
export async function pushUiStateEntries(
  entries: Record<string, string>,
  removeKeys: string[]
): Promise<boolean> {
  const response = await callUiStateRpc('SetUiState', SetUiStateRequestSchema, {
    entries,
    removeKeys,
  });
  return response !== null;
}
